import { useState, useEffect } from 'react';
import { X, ArrowLeftRight, ArrowDown, Hand } from 'lucide-react';
import { Button } from './ui/button';

const STORAGE_KEY = 'isp_gesture_tutorial_seen';

const GestureTutorialOverlay = () => {
  const [show, setShow] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    const seen = localStorage.getItem(STORAGE_KEY);
    // Show tutorial on mobile only, after a short delay
    if (!seen && window.innerWidth < 1024) {
      const timer = setTimeout(() => setShow(true), 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setShow(false);
  };

  const gestures = [
    {
      icon: ArrowLeftRight,
      title: 'Swipe to Switch Chats',
      desc: 'In AI Study Buddy, swipe left or right on the chat area to switch between your conversation sessions.',
      color: 'from-purple-500 to-indigo-500',
    },
    {
      icon: ArrowDown,
      title: 'Minimize Recording',
      desc: 'While recording a lecture, swipe down or tap the minimize button to collapse it into a floating widget. Keep recording while using other features!',
      color: 'from-pink-500 to-rose-500',
    },
    {
      icon: Hand,
      title: 'Quick Navigation',
      desc: 'Use the bottom navigation bar to jump between Home, Recording, AI Buddy, Exams, and Settings.',
      color: 'from-amber-500 to-orange-500',
    },
  ];

  if (!show) return null;

  const current = gestures[step];
  const Icon = current.icon;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-6" data-testid="gesture-tutorial">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={dismiss} />
      <div className="relative w-full max-w-sm bg-[#13131f] border border-white/10 rounded-3xl p-6 shadow-2xl animate-fadeIn">
        <button aria-label="Close"
          onClick={dismiss}
          className="absolute top-4 right-4 p-1.5 text-white/40 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          data-testid="gesture-tutorial-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="text-center mb-6">
          <div className={`w-16 h-16 mx-auto mb-4  bg-gradient-to-br ${current.color} flex items-center justify-center shadow-lg`}>
            <Icon className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-lg font-bold text-white mb-2">{current.title}</h3>
          <p className="text-sm text-white/60 leading-relaxed">{current.desc}</p>
        </div>

        {/* Step dots */}
        <div className="flex justify-center gap-2 mb-5">
          {gestures.map((_, i) => (
            <div key={`dot-${i}`} className={`h-1.5 rounded-full transition-all ${i === step ? 'w-6 bg-white' : 'w-1.5 bg-white/20'}`} />
          ))}
        </div>

        <div className="flex gap-3">
          {step < gestures.length - 1 ? (
            <>
              <Button
                variant="ghost"
                onClick={dismiss}
                className="flex-1 rounded-xl text-white/50 hover:text-white hover:bg-white/10"
                data-testid="gesture-tutorial-skip"
              >
                Skip
              </Button>
              <Button
                onClick={() => {
                  if (navigator.vibrate) navigator.vibrate(10);
                  setStep(s => s + 1);
                }}
                className={`flex-1 rounded-xl bg-gradient-to-r ${current.color} text-white font-medium`}
                data-testid="gesture-tutorial-next"
              >
                Next
              </Button>
            </>
          ) : (
            <Button
              onClick={dismiss}
              className="w-full rounded-xl bg-[#0A0A0A] text-white font-medium"
              data-testid="gesture-tutorial-done"
            >
              Got it!
            </Button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default GestureTutorialOverlay;
